/**
 * LES TRAVAUX CORRIGÉS D'UN ÉLÈVE — la liste, puis la copie annotée.
 *
 * Le fondateur, en recette (14 septembre 2026) : « dans Silex sur ordinateur
 * version web, impossible de charger les devoirs de la section apprentissage
 * annotés pour les visualiser. On voit juste la liste. Et donc pas possible
 * d'exporter en PDF, ni de s'assurer que les versions sont cohérentes. »
 *
 * Ce n'était pas un chargement qui échouait : la console n'avait AUCUNE route
 * vers les copies, et son client n'appelait jamais l'API des travaux. Cette
 * page est la page manquante.
 *
 * CE QUI EST BRANCHÉ : la liste (`GET /v1/tuteur/eleves/:id/copies`) et la copie
 * elle-même (`…/copies/:copieId`) — la transcription telle que l'élève l'a
 * confirmée, et la correction annotée question par question, la même que celle
 * qu'il lit dans son application. Une seule vérité sur un même travail.
 *
 * L'EXPORT PDF passe par l'IMPRESSION DU NAVIGATEUR, et c'est un choix, pas un
 * pis-aller. Le fondateur a demandé « de s'assurer que les versions sont
 * cohérentes » : un PDF fabriqué par le serveur serait une SECONDE mise en page
 * de la même copie, qui divergerait de l'écran au premier changement. Imprimer
 * la page elle-même garantit qu'il n'en existe qu'une. « Enregistrer en PDF »
 * est une destination d'impression offerte par tous les navigateurs.
 */
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { CopieDto, QuestionCorrigeeDto } from '@silex/shared';

import { api } from '../api/client';
import { EmptyState } from '../components/EmptyState';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { nomDocumentCopie } from '../lib/copie-export';
import { matiereLabel } from '../lib/suivi-format';
import { formatDate } from '../lib/tuteur-format';
import { useAsync } from '../lib/useAsync';

/** Ce que le verdict d'une question dit, en toutes lettres. */
const LIBELLE_VERDICT: Readonly<Record<string, string>> = {
  juste: 'Juste',
  a_ecrire: 'Écriture à revoir',
  partiel: 'Partiel',
  erreur: 'À revoir',
  pas_fait: 'Pas fait',
};

/** L'origine d'une copie, dite comme l'élève la connaît. */
const LIBELLE_ORIGINE: Readonly<Record<string, string>> = {
  defi: 'Défi du jour',
  devoir: 'Devoir photographié',
};

/* ─────────────────────────────── la liste ─────────────────────────────── */

export function CopiesElevePage() {
  const { eleveId = '' } = useParams<{ eleveId: string }>();
  /* MV-DEF-01 — le chargeur était recréé à chaque rendu, et l'effet de
     `useAsync` dépend de son identité : chaque réponse relançait la requête,
     et la page restait sur « Chargement » sans fin. Il est mémorisé sur ce qui
     le détermine ; `lib/chargeurs-memorises.test.ts` interdit la faute partout. */
  const copies = useAsync(useMemo(() => () => api.getCopiesEleve(eleveId), [eleveId]));

  return (
    <div className="content__inner">
      <p className="breadcrumb">
        <Link to="/eleves">Mes élèves</Link> / <Link to={`/eleves/${eleveId}`}>Fiche élève</Link>
      </p>
      <div className="page-head">
        <div>
          <h1>Travaux corrigés</h1>
          <p className="page-sub">
            Ce que l’élève a rendu, et la correction qu’il lit dans son application.
          </p>
        </div>
      </div>

      {copies.status === 'loading' ? <Chargement quoi="les travaux de cet élève" /> : null}
      {copies.status === 'error' ? <ErreurRessource message={copies.message} /> : null}
      {copies.status === 'ready' && copies.data.nbCopies === 0 ? (
        <EmptyState title="Aucun travail corrigé">
          Les travaux apparaissent ici une fois lus et corrigés.
        </EmptyState>
      ) : null}

      {copies.status === 'ready' && copies.data.nbCopies > 0 ? (
        <div className="list">
          {copies.data.copies.map((c) => (
            <Link key={c.id} className="li li--link" to={`/eleves/${eleveId}/copies/${c.id}`}>
              <span className="li-main">
                <span className="li-title">{c.titre ?? 'Travail sans titre'}</span>
                <span className="li-sub">
                  {matiereLabel(c.matiere)} · {LIBELLE_ORIGINE[c.origine] ?? c.origine} ·{' '}
                  {formatDate(c.creeLe)}
                  {c.note ? ` · ${c.note.globale.points}/${c.note.globale.sur}` : ''}
                </span>
              </span>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ────────────────────────────── une copie ─────────────────────────────── */

export function CopieAnnoteePage() {
  const { eleveId = '', copieId = '' } = useParams<{ eleveId: string; copieId: string }>();
  /* MV-DEF-01 — même faute, même correction : une requête, pas une boucle. */
  const copie = useAsync(
    useMemo(() => () => api.getCopieEleve(eleveId, copieId), [eleveId, copieId]),
  );

  return (
    <div className="content__inner">
      <p className="breadcrumb">
        <Link to="/eleves">Mes élèves</Link> / <Link to={`/eleves/${eleveId}`}>Fiche élève</Link> /{' '}
        <Link to={`/eleves/${eleveId}/copies`}>Travaux corrigés</Link>
      </p>

      {copie.status === 'loading' ? <Chargement quoi="cette copie" /> : null}
      {copie.status === 'error' ? <ErreurRessource message={copie.message} /> : null}
      {copie.status === 'ready' ? <Copie copie={copie.data} /> : null}
    </div>
  );
}

/**
 * Imprimer la page — et donc, au choix de l'utilisateur, l'enregistrer en PDF.
 *
 * Le titre du document devient le nom de fichier que le navigateur propose ; on
 * le remet ensuite, parce qu'il sert aussi d'étiquette d'onglet. `afterprint`
 * se déclenche que l'impression ait été lancée ou annulée.
 */
function imprimer(copie: CopieDto): void {
  const avant = document.title;
  document.title = nomDocumentCopie(copie);
  window.addEventListener(
    'afterprint',
    () => {
      document.title = avant;
    },
    { once: true },
  );
  window.print();
}

function Copie({ copie }: { copie: CopieDto }) {
  const corrigees = new Map<string, QuestionCorrigeeDto>();
  for (const ex of copie.correction?.exercices ?? []) {
    for (const q of ex.questions) corrigees.set(q.id, q);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{copie.titre ?? 'Travail sans titre'}</h1>
          <p className="page-sub">
            {matiereLabel(copie.matiere)} · {LIBELLE_ORIGINE[copie.origine] ?? copie.origine} ·{' '}
            {formatDate(copie.creeLe)}
          </p>
        </div>
        {copie.note ? (
          <p className="lede">
            {copie.note.globale.points}/{copie.note.globale.sur}
            {copie.note.globale.aFaire > 0 ? ` · ${copie.note.globale.aFaire} à faire` : ''}
          </p>
        ) : null}
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => imprimer(copie)}>
          Imprimer ou enregistrer en PDF
        </button>
      </div>

      {copie.transcription === null ? (
        <EmptyState title="Rien à montrer">
          Ce travail n’a pas encore de transcription lue.
        </EmptyState>
      ) : null}

      {(copie.transcription?.exercices ?? []).map((ex) => (
        <section className="card" key={ex.numero}>
          <h3>Exercice {ex.numero}</h3>
          {ex.consigne ? <p className="card-sub">{ex.consigne}</p> : null}
          {ex.contexte ? <p className="note-line">{ex.contexte}</p> : null}

          {ex.questions.map((q) => {
            const c = corrigees.get(q.id);
            return (
              <div className="field field--bloc" key={q.id}>
                <p className="lbl">
                  {q.etiquette ?? q.id}
                  {c ? ` — ${LIBELLE_VERDICT[c.verdict] ?? c.verdict}` : ''}
                </p>
                {q.enonce ? <p className="note-line">{q.enonce}</p> : null}

                {/* Ce que l'élève a écrit, ligne par ligne, tel qu'il l'a rendu. */}
                {q.lignes.map((l) => (
                  <p className="controle__extrait" key={l.n}>
                    {l.texte}
                  </p>
                ))}
                {(q.trous ?? []).map((t) => (
                  <p className="controle__extrait" key={`t${t.n}`}>
                    Trou {t.n} : {t.reponse.trim() === '' ? '—' : t.reponse}
                  </p>
                ))}
                {q.reponse ? <p className="controle__extrait">{q.reponse}</p> : null}

                {/* L'annotation du correcteur — la même que l'élève lit. */}
                {c?.commentaire ? <p className="hint">{c.commentaire}</p> : null}
                {(c?.lignes ?? []).map((l) =>
                  l.note ? (
                    <p className="controle__marque" key={`n${l.n}`}>
                      Ligne {l.n} — {l.note}
                    </p>
                  ) : null,
                )}
              </div>
            );
          })}
        </section>
      ))}

      {copie.correction?.bilan ? (
        <section className="card card--calm">
          <h3>Le bilan</h3>
          {copie.correction.bilan.coupDOeil.map((c, i) => (
            <p className="note-line" key={i}>
              {c.texte}
            </p>
          ))}
          {copie.correction.bilan.motDeLaFin ? (
            <p className="lede">{copie.correction.bilan.motDeLaFin}</p>
          ) : null}
        </section>
      ) : null}
    </>
  );
}
